import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Alert, Loader } from '@mantine/core';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatAmount, formatInt, formatMoney, type StatsView, type StatsViewResponse } from '@parts/shared';
import { errorText, getJson } from '../../api';
import { PageHeader } from '../../components/layout/PageHeader';
import { t, tn, useI18n, type Lang } from '../../i18n';
import { useAppState } from '../../state';
import classes from './StatsScreen.module.css';

const MONTHS: Record<Lang, string[]> = {
  ru: ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  zh: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
};

let monthLang: Lang = 'ru';

const monthLabel = (m: string): string => {
  const [y, mm] = m.split('-');
  const name = MONTHS[monthLang][Number(mm) - 1] ?? mm;
  return monthLang === 'zh' ? `${String(y).slice(2)}年${name}` : `${name} ${String(y).slice(2)}`;
};

/** Цвета графиков (переменные из global.css — подходят к обеим темам). */
const C = {
  accent: 'var(--accent)',
  blue: 'var(--blue)',
  green: 'var(--green)',
  amber: 'var(--amber)',
  violet: 'var(--violet)',
  red: 'var(--red)'
};
const LINE_COLORS = [C.accent, C.green, C.amber, C.violet, C.red];

const axis = { stroke: 'var(--text-3)', fontSize: 11.5, tickLine: false, axisLine: false } as const;

const tooltipStyle = {
  contentStyle: {
    background: 'var(--surface)',
    border: '1px solid var(--line-strong)',
    borderRadius: 10,
    boxShadow: 'var(--shadow-pop)',
    fontSize: 12.5,
    padding: '8px 12px'
  },
  labelStyle: { color: 'var(--text-3)', marginBottom: 4 },
  itemStyle: { color: 'var(--text)', padding: '1px 0' },
  cursor: { fill: 'var(--surface-2)', stroke: 'var(--line-strong)' }
};

/** Короткая запись больших сумм для оси: 12 тыс, 1,2 млн. */
function short(v: number): string {
  const a = Math.abs(v);
  if (a >= 1_000_000) return t('{n} млн', { n: (v / 1_000_000).toFixed(1).replace('.', ',') });
  if (a >= 1_000) return t('{n} тыс', { n: Math.round(v / 1_000) });
  return String(Math.round(v));
}

/** Изменение последнего месяца к предыдущему, % (или null, если сравнивать не с чем). */
function delta(values: number[]): number | null {
  if (values.length < 2) return null;
  const prev = values[values.length - 2]!;
  const last = values[values.length - 1]!;
  if (!prev) return null;
  return ((last - prev) / Math.abs(prev)) * 100;
}

/** Экран «Статистика»: суммы заказов, выручка и маржа по месяцам, движение цен фабрики. */
export function StatsScreen() {
  const { lang, tx } = useI18n();
  monthLang = lang;
  const { state } = useAppState();
  const [stats, setStats] = useState<StatsView | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await getJson<StatsViewResponse>('/api/views/stats');
      setStats(res.stats);
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, state?.snapshot.version]);

  const head = <PageHeader title={t('Статистика')} subtitle={t('по всем запросам и закупкам')} />;

  if (error)
    return (
      <div className={classes.page}>
        {head}
        <div className={classes.body}>
          <Alert color="red" variant="light" title={t('Не удалось загрузить статистику')}>
            {tx(error)}
          </Alert>
        </div>
      </div>
    );
  if (!stats)
    return (
      <div className={classes.page}>
        {head}
        <div className={classes.loading}>
          <Loader size="sm" />
          {t('Считаю статистику…')}
        </div>
      </div>
    );

  const months = stats.months.map((m) => ({ ...m, label: monthLabel(m.month) }));
  const priceData = buildPriceSeries(stats);
  const topMax = Math.max(1, ...stats.top.map((t) => t.revenue));

  const kpis: Kpi[] = [
    {
      label: t('Запросов'),
      value: formatInt(stats.totals.requests),
      hint: t('в работе {n}', { n: formatInt(stats.totals.openRequests) }),
      series: months.map((m) => m.requests),
      color: C.accent
    },
    {
      label: t('Сумма запросов'),
      value: formatMoney(stats.totals.demandAmount),
      hint: tn(stats.totals.positions, ['позиция', 'позиции', 'позиций']),
      series: months.map((m) => m.demand),
      color: C.blue
    },
    {
      label: t('Выручка по закупленному'),
      value: formatMoney(stats.totals.revenue),
      hint: t('{n} шт закуплено', { n: formatInt(stats.totals.boughtQty) }),
      series: months.map((m) => m.revenue),
      color: C.green
    },
    {
      label: t('Маржа'),
      value: formatMoney(stats.totals.margin),
      hint: t('{n} % от выручки', { n: formatAmount(stats.totals.marginPct) }),
      series: months.map((m) => m.margin),
      color: C.violet
    }
  ];

  return (
    <div className={classes.page}>
      {head}
      <div className={classes.body}>
        <div className={classes.kpis}>
          {kpis.map((k, i) => (
            <KpiCard key={k.label} kpi={k} id={`kpi${i}`} />
          ))}
        </div>

        <div className={classes.grid}>
          <Panel title={t('Суммы запросов по месяцам')} note="CNY">
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="demandFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="var(--accent)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="var(--line)" />
                <XAxis dataKey="label" {...axis} />
                <YAxis {...axis} width={64} tickFormatter={short} />
                <Tooltip {...tooltipStyle} formatter={(v) => formatMoney(Number(v ?? 0))} />
                <Area type="monotone" dataKey="demand" name={t('Запрошено')} stroke={C.accent} strokeWidth={2} fill="url(#demandFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title={t('Выручка, затраты и маржа')} note="CNY">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={3}>
                <CartesianGrid vertical={false} stroke="var(--line)" />
                <XAxis dataKey="label" {...axis} />
                <YAxis {...axis} width={64} tickFormatter={short} />
                <Tooltip {...tooltipStyle} formatter={(v) => formatMoney(Number(v ?? 0))} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: 'var(--text-2)', paddingTop: 6 }} />
                <Bar dataKey="revenue" name={t('Выручка')} fill={C.green} radius={[4, 4, 0, 0]} maxBarSize={22} />
                <Bar dataKey="cost" name={t('Затраты')} fill={C.amber} radius={[4, 4, 0, 0]} maxBarSize={22} />
                <Bar dataKey="margin" name={t('Маржа')} fill={C.accent} radius={[4, 4, 0, 0]} maxBarSize={22} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title={t('Цены фабрики')} note={t('CNY за штуку')}>
            {priceData.length ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={priceData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="var(--line)" />
                  <XAxis dataKey="label" {...axis} />
                  <YAxis {...axis} width={64} />
                  <Tooltip {...tooltipStyle} formatter={(v) => formatAmount(Number(v ?? 0))} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: 'var(--text-2)', paddingTop: 6 }} />
                  {stats.prices.slice(0, 5).map((p, i) => (
                    <Line
                      key={p.art}
                      type="monotone"
                      dataKey={p.art}
                      name={p.art}
                      stroke={LINE_COLORS[i]}
                      strokeWidth={2}
                      dot={{ r: 2.5, strokeWidth: 0, fill: LINE_COLORS[i] }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className={classes.empty}>{t('Пока мало данных: цена фабрики должна быть внесена хотя бы за два месяца.')}</div>
            )}
          </Panel>

          <Panel title={t('Изменение цен фабрики')}>
            <div className={classes.tableWrap}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th>OEM</th>
                    <th>{t('Наименование')}</th>
                    <th className={classes.num}>{t('Было')}</th>
                    <th className={classes.num}>{t('Стало')}</th>
                    <th className={classes.num}>{t('Изменение')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.prices.map((p) => (
                    <tr key={p.art}>
                      <td className={classes.strong}>{p.art}</td>
                      <td className={classes.muted}>{p.name}</td>
                      <td className={classes.num}>{formatAmount(p.first)}</td>
                      <td className={classes.num}>{formatAmount(p.last)}</td>
                      <td className={classes.num}>
                        <span className={classes.delta} data-up={p.changePct > 0 || undefined}>
                          {p.changePct > 0 ? '+' : ''}
                          {formatAmount(p.changePct)} %
                        </span>
                      </td>
                    </tr>
                  ))}
                  {!stats.prices.length && (
                    <tr>
                      <td colSpan={5} className={classes.muted}>
                        {t('Изменений цен пока нет.')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>

        <Panel title={t('Топ позиций по закупленной сумме')}>
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>OEM</th>
                  <th>{t('Наименование')}</th>
                  <th className={classes.num}>{t('Закуплено, шт')}</th>
                  <th className={classes.num}>{t('Выручка, CNY')}</th>
                  <th className={classes.shareHead}>{t('Доля')}</th>
                  <th className={classes.num}>{t('Маржа, CNY')}</th>
                </tr>
              </thead>
              <tbody>
                {stats.top.map((t) => (
                  <tr key={t.art}>
                    <td className={classes.strong}>{t.art}</td>
                    <td className={classes.muted}>{t.name}</td>
                    <td className={classes.num}>{formatInt(t.qty)}</td>
                    <td className={classes.num}>{formatAmount(t.revenue)}</td>
                    <td className={classes.share}>
                      <span className={classes.shareBar}>
                        <span style={{ width: `${(t.revenue / topMax) * 100}%` }} />
                      </span>
                    </td>
                    <td className={classes.num} data-bad={t.margin < 0 || undefined}>
                      {formatAmount(t.margin)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  );
}

interface Kpi {
  label: string;
  value: string;
  hint: string;
  series: number[];
  color: string;
}

/** Показатель: значение, изменение к прошлому месяцу и маленький график по месяцам. */
function KpiCard({ kpi, id }: { kpi: Kpi; id: string }) {
  const d = delta(kpi.series);
  const data = kpi.series.map((v, i) => ({ i, v }));
  return (
    <div className={classes.kpi}>
      <div className={classes.kpiHead}>
        <span className={classes.kpiDot} style={{ background: kpi.color }} />
        <span className={classes.kpiLabel}>{kpi.label}</span>
      </div>
      <div className={classes.kpiValue}>{kpi.value}</div>
      <div className={classes.kpiFoot}>
        <span className={classes.kpiHint}>{kpi.hint}</span>
        {d !== null && (
          <span className={classes.kpiDelta} data-down={d < 0 || undefined} title={t('Последний месяц к предыдущему')}>
            {d > 0 ? '+' : ''}
            {Math.round(d)} %
          </span>
        )}
      </div>
      {data.length > 1 && (
        <div className={classes.spark}>
          <ResponsiveContainer width="100%" height={44}>
            <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`${id}-fill`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={kpi.color} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={kpi.color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke={kpi.color} strokeWidth={1.8} fill={`url(#${id}-fill)`} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function Panel({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section className={classes.panel}>
      <div className={classes.panelHead}>
        <h2 className={classes.panelTitle}>{title}</h2>
        {note && <span className={classes.panelNote}>{note}</span>}
      </div>
      {children}
    </section>
  );
}

/** Цены нескольких OEM в одной таблице по месяцам — для графика. */
function buildPriceSeries(stats: StatsView): Record<string, string | number>[] {
  const months = new Set<string>();
  for (const p of stats.prices.slice(0, 5)) for (const point of p.points) months.add(point.month);
  return [...months]
    .sort()
    .map((m) => {
      const row: Record<string, string | number> = { label: monthLabel(m) };
      for (const p of stats.prices.slice(0, 5)) {
        const point = p.points.find((x) => x.month === m);
        if (point) row[p.art] = point.price;
      }
      return row;
    });
}
